import { createContext, useState, useContext, useLayoutEffect } from 'react'

import useMapDataContext from '@contexts/mapDataContext'
import { useAuthContext } from './authContext'
import { useNavigate } from 'react-router-dom'
import snakecaseKeys from 'snakecase-keys'
import useLoading from '@hooks/useLoading'
import axios from '@utils/webRequests'
import { Toast } from '@base-ui/react'

const defaultState = {
  isActive: false,
  isOnline: false,
  tempStatus: new Set(),
  hostLocation: new Set(),
  position: new Set(),
  age: [18, 65],
  endowmentSize: [0, 13],
}

const NearbyFilterContext = createContext();
const NearbyFilterProvider = ({ children }) => {
  const { nearbyProfileRefreshRequest, isProfilesReloading } = useMapDataContext()
  const { state: authState, handleInstantUpdate } = useAuthContext()
  const toastManager = Toast.useToastManager()
  const navigate = useNavigate()
  const { isLoading, isLoadingDelayed, setIsLoading } = useLoading()
  const [isFilterChanged, setIsFilterChanged] = useState(false)
  const [savedState, setSavedState] = useState(defaultState)
  const [filterState, setFilterState] = useState(defaultState)

  // Update filterState and savedState when user loads/isChanged
  useLayoutEffect(() => {
    if (authState?.user?.profileFilters) {
      const newFilterState = authState?.user?.profileFilters
      // transform enums to sets
      newFilterState.tempStatus = new Set(newFilterState.tempStatus)
      newFilterState.hostLocation = new Set(newFilterState.hostLocation)
      newFilterState.position = new Set(newFilterState.position)
      // remove "unselected" values
      newFilterState.tempStatus.delete('temp_status_unselected')
      newFilterState.hostLocation.delete('host_location_unselected')
      newFilterState.position.delete('position_unselected')
      // set age to default if null
      if (!newFilterState.age) {
        newFilterState.age = defaultState.age
      }
      // set endowment size to default if null
      if (!newFilterState.endowmentSize) {
        newFilterState.endowmentSize = defaultState.endowmentSize
      } else {
        // transform metric to imperial
        newFilterState.endowmentSize = newFilterState?.endowmentSize?.map?.(cm => parseFloat((cm/2.54).toFixed(1))) // cm to inches
      }
      // save to local state
      setFilterState(authState?.user?.profileFilters)
      setSavedState(authState?.user?.profileFilters)
    }
  }, [authState?.user?.profileFilters])

  // Compare the filter state for changes whenever the user changes the values
  useLayoutEffect(() => compareFilterState(), [filterState])

  const compareFilterState = () => {
    let isChanged = false
    for (let key in filterState) {
      switch (true) {
        case (typeof filterState[key] === 'boolean'):
          if (filterState[key] !== savedState[key]) {
            isChanged = true
          }
          break
        case (filterState[key] instanceof Set):
          if (filterState[key].symmetricDifference(savedState[key]).size > 0) {
            isChanged = true
          }
          break
        case (filterState[key] instanceof Array):
          if (filterState[key][0] !== savedState[key][0] || filterState[key][1] !== savedState[key][1]) {
            isChanged = true
          }
          break
        default:
          console.error('UNKNOWN FILTER VALUE')
      }
    }
    setIsFilterChanged(isChanged)
  }

  const toggleActive = () => {
    setFilterState({ ...filterState, isActive: !filterState?.isActive })
  }

  const onToggleButtonClick = (event, name, value) => {
    event.preventDefault()
    if (isLoading) return;
    if (!filterState[name] || !(filterState[name] instanceof Set)) return;
    const prevSet = new Set(filterState[name])
    if (prevSet?.has?.(value)) {
      prevSet.delete(value)
    } else {
      prevSet.add(value)
    }
    setFilterState({ ...filterState, isActive: true, [name]: prevSet })
  }

  const handleRadioChange = (e, newVal) => {
    if (isLoading) return;
    // WARNING: e.preventDefault will cause the switch/radio to require double clicking
    setFilterState({ ...filterState, isActive: true, [e.target.name]: newVal })
  }

  const handleSliderChange = (name, newVal) => {
    if (isLoading) return;
    setFilterState({ ...filterState, isActive: true, [name]: newVal })
  }

  const resetAge = () => handleSliderChange('age', [18, 65])
  const resetEndowmentSize = () => handleSliderChange('endowmentSize', [0, 13])

  const parseRequestData = () => {
    const requestData = {...filterState}
    requestData.tempStatus = [...requestData?.tempStatus]
    requestData.hostLocation = [...requestData?.hostLocation]
    requestData.position = [...requestData?.position]
    // set age to null for backend if value unset
    if (requestData.age[0] === 18 && requestData.age[1] === 65) {
      requestData.age = null
    }
    // set endowmentSize to null for backend if value unset
    if (requestData.endowmentSize[0] === 0 && requestData.endowmentSize[1] === 13) {
      requestData.endowmentSize = null
    } else {
      requestData.endowmentSize = requestData?.endowmentSize?.map?.(inches => inches*2.54)
    }
    return snakecaseKeys(requestData)
  }

  const handleNearbyFilterSubmit = (e) => {
    e.preventDefault()
    if (isLoading) return;
    setIsLoading(true)
    return axios.put('/api/my/profiles/set_filters', parseRequestData())
      .then(res =>  {
        // check that user exists
        if (res?.data?.uuid) {
          // add success toast
          toastManager.add({ text: 'saved filters' })
          // update user in auth state with returned data
          handleInstantUpdate(res.data)
          // navigate user back to map
          navigate('/map', { replace: true })
          // request new users on map with updated filters
          nearbyProfileRefreshRequest({ removeOldUsersImmediately: false })
            ?.catch?.(() => toastManager.add({ text: 'failed to get new profiles' }))
        }
      })
      .catch(err => {
        toastManager.add({ text: 'failed to save filters' })
        console.error(err)
      })
      .finally(() => setIsLoading(false))
  }

  return (
    <NearbyFilterContext.Provider value={{
      toggleActive,
      onToggleButtonClick,
      handleRadioChange,
      handleSliderChange,
      resetAge,
      resetEndowmentSize,
      handleNearbyFilterSubmit,
      savedState,
      filterState,
      isFilterChanged,
      isFilterLoading: isLoading,
      isFilterLoadingDelayed: isLoadingDelayed,
    }}>
      {children}
    </NearbyFilterContext.Provider>
  )
}

const useNearbyFilterContext = () => useContext(NearbyFilterContext)

export default useNearbyFilterContext

export { NearbyFilterContext, NearbyFilterProvider, useNearbyFilterContext }
